import React, { createContext, useContext, useState, useEffect } from 'react';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};

const defaultSettings = {
  siteName: 'vetoRDV',
  tagline: 'Trouvez un vétérinaire et prenez rendez-vous gratuitement en ligne',
  contact: {
    email: 'contact@vetordv.fr',
    phone: '06 74 93 15 19',
    address: '2 GRAND RUE',
    city: 'BEAUVOISIN',
    postalCode: '30640',
    country: 'France',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.2!2d4.3!3d43.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDQyJzAwLjAiTiA0wrAxOCcwMC4wIkU!5e0!3m2!1sfr!2sfr',
    openingHours: [
      { day: 'Lundi - Vendredi', hours: '9h00 - 18h00' },
      { day: 'Samedi', hours: '9h00 - 12h00' },
      { day: 'Dimanche', hours: 'Fermé' },
    ],
  },
  social: {
    facebook: 'https://facebook.com/vetordv',
    instagram: 'https://instagram.com/vetordv',
    twitter: 'https://twitter.com/vetordv',
    linkedin: 'https://linkedin.com/company/vetordv',
    youtube: '',
    tiktok: '',
  },
  theme: {
    primaryColor: '#22c55e',
    primaryColorDark: '#16a34a',
    primaryColorLight: '#86efac',
    secondaryColor: '#3b82f6',
    accentColor: '#f59e0b',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    textColorLight: '#6b7280',
  },
  homepage: {
    heroTitle: 'Votre rendez-vous vétérinaire',
    heroTitleHighlight: 'en quelques clics !',
    heroSubtitle: 'Simple, rapide et gratuit',
    heroImage: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800',
    heroButtonText: 'Rechercher',
    heroButtonLink: '/recherche',
    features: [
      { id: '1', icon: 'search', title: 'Trouvez un vétérinaire', description: 'parmi les 7000 cliniques listées sur notre site internet', active: true },
      { id: '2', icon: 'calendar', title: 'Sélectionnez un créneau', description: 'directement pour prendre rendez-vous en ligne', active: true },
      { id: '3', icon: 'bell', title: 'Recevez un rappel', description: 'par SMS la veille du rendez-vous pour ne pas l\'oublier', active: true },
    ],
    showStats: true,
    stats: [
      { id: '1', value: '7000+', label: 'Cliniques', active: true },
      { id: '2', value: '50000+', label: 'Rendez-vous/mois', active: true },
      { id: '3', value: '4.8/5', label: 'Satisfaction', active: true },
    ],
  },
  // Promotion settings
  promotion: {
    enabled: true,
    trialMonths: 2,
    trialText: "2 mois offerts",
    description: "Testez vetoRDV gratuitement. Carte bancaire requise, premier prélèvement après la période d'essai.",
    bannerEnabled: true,
    bannerText: "🎉 OFFRE DE LANCEMENT : {months} MOIS OFFERTS pour les vétérinaires ! Inscription gratuite, premier prélèvement dans {months} mois.",
  },
  pricing: {
    patientFree: true,
    plans: [
      {
        id: 'essentiel',
        name: 'Essentiel',
        price: 29,
        color: 'green',
        maxVets: 1,
        appointmentsLimit: 100,
        features: [
          'Agenda simple',
          'Rappels email',
          '100 RDV/mois',
          '1 vétérinaire max',
        ],
        notIncluded: [
          'Rappels SMS',
          'Fiches patient avancées',
          'Statistiques avancées',
          'Sync agenda externe',
        ],
        description: 'Idéal pour démarrer',
        popular: false,
        active: true
      },
      {
        id: 'pro',
        name: 'Pro',
        price: 49,
        color: 'blue',
        maxVets: 2,
        appointmentsLimit: 200,
        features: [
          'Agenda complet',
          'Rappels SMS + Email',
          '200 RDV/mois',
          'Jusqu\'à 2 vétérinaires',
          'Fiches patient',
          '📹 Téléconsultation vidéo',
        ],
        notIncluded: [
          'Statistiques avancées',
          'Sync agenda externe',
        ],
        description: 'Le plus populaire',
        popular: true,
        active: true
      },
      {
        id: 'multi',
        name: 'Multi',
        price: 79,
        color: 'purple',
        maxVets: 5,
        appointmentsLimit: 500,
        features: [
          'Agenda complet',
          'Rappels SMS + Email',
          '500 RDV/mois',
          'Jusqu\'à 5 vétérinaires',
          'Fiches patient',
          '📹 Téléconsultation vidéo',
          'Statistiques avancées',
          'Sync agenda externe',
        ],
        notIncluded: [],
        description: 'Solution complète',
        popular: false,
        active: true
      }
    ]
  },
  features: {
    enableReviews: true,
    enableGeolocation: true,
    enableSmsReminders: true,
    enableEmailReminders: true,
  },
  seo: {
    metaTitle: 'vetoRDV - Prenez rendez-vous chez un vétérinaire en ligne',
    metaDescription: 'Trouvez un vétérinaire près de chez vous et prenez rendez-vous gratuitement en ligne. Simple, rapide et gratuit pour les patients.',
  },
  header: {
    logoText: 'VETORDV',
    logoTextPart1: 'VETO',
    logoTextPart2: 'RDV',
    showLogo: true,
    logoType: 'default',
    customLogoUrl: '',
    logoColor: '#22c55e',
    logoColorDark: '#16a34a',
    navLinks: [
      { id: '1', label: 'Conseils', path: '/conseils', icon: 'none', active: true },
      { id: '2', label: 'Espace patient', path: '/espace-patient', icon: 'user', active: true },
      { id: '3', label: 'Espace vétérinaire', path: '/espace-veterinaire', icon: 'stethoscope', active: true },
    ],
  },
  footer: {
    description: 'Trouvez un vétérinaire et prenez rendez-vous gratuitement en ligne.',
    copyrightText: 'vetoRDV® - Tous droits réservés',
    copyrightStartYear: '2017',
    slogan: 'Fait avec ❤️ pour les animaux',
    showSocialLinks: true,
    showAdminLink: true,
    sections: [
      {
        id: '1',
        title: 'Navigation',
        links: [
          { id: '1', label: 'Trouver un vétérinaire', path: '/recherche' },
          { id: '2', label: 'Conseils', path: '/conseils' },
          { id: '3', label: 'Espace patient', path: '/espace-patient' },
          { id: '4', label: 'Espace vétérinaire', path: '/espace-veterinaire' },
        ]
      },
      {
        id: '2',
        title: 'Support',
        links: [
          { id: '1', label: 'Contact & FAQ', path: '/contact' },
        ]
      },
      {
        id: '3',
        title: 'Légal',
        links: [
          { id: '1', label: 'Mentions légales', path: '/mentions-legales' },
          { id: '2', label: 'CGU', path: '/cgu' },
          { id: '3', label: 'Confidentialité', path: '/confidentialite' },
          { id: '4', label: 'Vos droits RGPD', path: '/rgpd' },
        ]
      }
    ]
  },
  legal: {
    mentionsLegales: {
      title: 'Mentions Légales',
      lastUpdate: 'Février 2025',
      sections: [
        {
          id: '1',
          title: '1. Éditeur du site',
          content: `Le site vetoRDV est édité par :
**vetoRDV SAS WUXI**
Société par Actions Simplifiée
Siège social : 2 GRAND RUE, 30640 BEAUVOISIN
SIRET : 824 896 278 00010
RCS Nîmes

**Directeur de la publication :** Eric MEZZASALMA
**Contact :** contact@vetordv.fr`
        },
        {
          id: '2',
          title: '2. Hébergement',
          content: `**Nom de domaine enregistré chez :**
IONOS SE
Elgendorfer Str. 57, 56410 Montabaur, Allemagne

**Hébergement du site :**
Emergent
Services d'hébergement cloud`
        },
        {
          id: '3',
          title: '3. Propriété intellectuelle',
          content: `L'ensemble des contenus présents sur le site vetoRDV (textes, images, logos, icônes, éléments graphiques, etc.) sont protégés par les dispositions du Code de la Propriété Intellectuelle. Toute reproduction, représentation, modification ou adaptation, totale ou partielle, sans l'autorisation expresse et préalable de vetoRDV est strictement interdite.`
        },
        {
          id: '4',
          title: '4. Responsabilité',
          content: `vetoRDV s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, vetoRDV ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site.`
        },
        {
          id: '5',
          title: '5. Liens hypertextes et affiliation',
          content: `Le site vetoRDV peut contenir des liens hypertextes vers d'autres sites. vetoRDV n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.

**Programme d'affiliation :** La boutique vetoRDV participe au Programme Partenaires d'Amazon EU, un programme d'affiliation conçu pour permettre à des sites de percevoir une rémunération grâce à la création de liens vers Amazon.fr. En tant que Partenaire Amazon, nous réalisons un bénéfice sur les achats remplissant les conditions requises.`
        },
        {
          id: '6',
          title: '6. Droit applicable',
          content: `Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux de Nîmes seront seuls compétents.`
        }
      ]
    },
    cgu: {
      title: 'Conditions Générales d\'Utilisation',
      lastUpdate: 'Janvier 2025',
      sections: [
        {
          id: '1',
          title: '1. Objet',
          content: `Les présentes Conditions Générales d'Utilisation (CGU) ont pour objet de définir les modalités d'accès et d'utilisation du site vetoRDV, plateforme de mise en relation entre les propriétaires d'animaux ("Patients") et les professionnels vétérinaires ("Vétérinaires").`
        },
        {
          id: '2',
          title: '2. Acceptation des CGU',
          content: `L'utilisation du site vetoRDV implique l'acceptation pleine et entière des présentes CGU. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser ce site.`
        },
        {
          id: '3',
          title: '3. Services proposés',
          content: `vetoRDV propose les services suivants :
• Recherche de vétérinaires par localisation et spécialité
• Prise de rendez-vous en ligne
• Rappels automatiques par SMS et email
• Gestion des animaux de compagnie
• Conseils et articles informatifs`
        },
        {
          id: '4',
          title: '4. Inscription',
          content: `**Pour les Patients :** L'inscription est gratuite. L'utilisateur s'engage à fournir des informations exactes et à les maintenir à jour.

**Pour les Vétérinaires :** L'inscription est soumise à validation par notre équipe. Le Vétérinaire doit justifier de son inscription à l'Ordre des Vétérinaires. L'abonnement est de 35€/mois pour 100 rendez-vous.`
        },
        {
          id: '5',
          title: '5. Obligations des utilisateurs',
          content: `Les utilisateurs s'engagent à :
• Utiliser le site de manière loyale et conforme à sa destination
• Ne pas usurper l'identité d'un tiers
• Respecter les rendez-vous pris ou les annuler dans un délai raisonnable
• Ne pas publier de contenu illicite, diffamatoire ou contraire aux bonnes mœurs`
        },
        {
          id: '6',
          title: '6. Responsabilité',
          content: `vetoRDV agit en qualité d'intermédiaire technique et ne saurait être tenu responsable des actes médicaux pratiqués par les Vétérinaires référencés sur la plateforme.`
        }
      ]
    },
    confidentialite: {
      title: 'Politique de Confidentialité',
      lastUpdate: 'Janvier 2025',
      sections: [
        {
          id: '1',
          title: '1. Introduction',
          content: `vetoRDV s'engage à protéger la vie privée de ses utilisateurs. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).`
        },
        {
          id: '2',
          title: '2. Données collectées',
          content: `Nous collectons les données suivantes :
• **Données d'identification :** nom, prénom, email, téléphone
• **Données de connexion :** adresse IP, type de navigateur, pages visitées
• **Données relatives aux animaux :** nom, espèce, race, âge
• **Données de géolocalisation :** avec votre consentement
• **Données de paiement :** pour les abonnements vétérinaires`
        },
        {
          id: '3',
          title: '3. Finalités du traitement',
          content: `Vos données sont utilisées pour :
• Gérer votre compte et vos rendez-vous
• Vous envoyer des rappels de rendez-vous
• Améliorer nos services
• Vous envoyer des communications marketing (avec votre consentement)
• Respecter nos obligations légales`
        },
        {
          id: '4',
          title: '4. Vos droits',
          content: `Conformément au RGPD, vous disposez des droits suivants :
• **Droit d'accès :** obtenir une copie de vos données
• **Droit de rectification :** corriger vos données
• **Droit à l'effacement :** demander la suppression de vos données
• **Droit à la portabilité :** récupérer vos données dans un format lisible
• **Droit d'opposition :** vous opposer à certains traitements
• **Droit à la limitation :** limiter le traitement de vos données

Pour exercer vos droits, utilisez notre formulaire dédié accessible sur la page /rgpd ou contactez-nous à : dpo@vetordv.fr`
        },
        {
          id: '5',
          title: '5. Cookies',
          content: `Nous utilisons des cookies pour améliorer votre expérience. Vous pouvez gérer vos préférences de cookies à tout moment via le bandeau cookies ou les paramètres de votre navigateur.

**Types de cookies utilisés :**
• Cookies essentiels : nécessaires au fonctionnement du site
• Cookies analytiques : pour améliorer nos services (avec votre consentement)
• Cookies marketing : pour personnaliser les publicités (avec votre consentement)`
        },
        {
          id: '6',
          title: '6. Durée de conservation',
          content: `Vos données personnelles sont conservées pendant :
• **Données de compte :** durée de l'utilisation du service + 3 ans après fermeture
• **Données de rendez-vous :** 5 ans (obligations légales santé animale)
• **Données de facturation :** 10 ans (obligations comptables)
• **Logs de connexion :** 1 an`
        },
        {
          id: '7',
          title: '7. Contact et réclamations',
          content: `Pour toute question relative à cette politique ou à vos données personnelles :

**Responsable du traitement :**
vetoRDV SAS WUXI
2 GRAND RUE, 30640 BEAUVOISIN
Email : dpo@vetordv.fr

**Formulaire RGPD :** Accédez à notre formulaire dédié pour exercer vos droits : /rgpd

**Réclamation CNIL :** Vous pouvez déposer une réclamation auprès de la CNIL : www.cnil.fr`
        }
      ]
    }
  }
};

// Version for forcing refresh of legal data when updated
const LEGAL_DATA_VERSION = "2026-03-06-v1";
// Version for forcing refresh of header settings
const HEADER_VERSION = "2026-03-06-v1";

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('vetordv_site_settings');
    const savedVersion = localStorage.getItem('vetordv_legal_version');
    const savedHeaderVersion = localStorage.getItem('vetordv_header_version');
    
    // Force refresh if legal version or header version has changed
    if (savedVersion !== LEGAL_DATA_VERSION || savedHeaderVersion !== HEADER_VERSION) {
      localStorage.removeItem('vetordv_site_settings');
      localStorage.setItem('vetordv_legal_version', LEGAL_DATA_VERSION);
      localStorage.setItem('vetordv_header_version', HEADER_VERSION);
      return defaultSettings;
    }
    
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with default settings to ensure all new properties exist
      return {
        ...defaultSettings,
        ...parsed,
        // Deep merge for nested objects
        contact: { ...defaultSettings.contact, ...parsed.contact },
        social: { ...defaultSettings.social, ...parsed.social },
        theme: { ...defaultSettings.theme, ...parsed.theme },
        homepage: { ...defaultSettings.homepage, ...parsed.homepage },
        // Always use default header settings (navLinks should come from code)
        header: defaultSettings.header,
        footer: { ...defaultSettings.footer, ...parsed.footer },
        // Always use default legal settings (from code) - don't cache
        legal: defaultSettings.legal,
      };
    }
    return defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('vetordv_site_settings', JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const updateContactSettings = (contact) => {
    setSettings(prev => ({ ...prev, contact: { ...prev.contact, ...contact } }));
  };

  const updateSocialSettings = (social) => {
    setSettings(prev => ({ ...prev, social: { ...prev.social, ...social } }));
  };

  const updatePricingSettings = (pricing) => {
    setSettings(prev => ({ ...prev, pricing: { ...prev.pricing, ...pricing } }));
  };

  const updateFeatureSettings = (features) => {
    setSettings(prev => ({ ...prev, features: { ...prev.features, ...features } }));
  };

  const resetToDefault = () => {
    setSettings(defaultSettings);
  };

  return (
    <SiteSettingsContext.Provider value={{
      settings,
      updateSettings,
      updateContactSettings,
      updateSocialSettings,
      updatePricingSettings,
      updateFeatureSettings,
      resetToDefault,
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export default SiteSettingsProvider;
